import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';
import { DEV_WRITE_AND_SOURCE_KEYS, MONARCH_SOURCE_ID, MONARCH_TOKEN } from '~/constants/config/common';
import clientSide from '~/helpers/clientSide';
import { getQuoteId } from '~/helpers/getQuoteId';
import { getUtmParamsFromCookie } from '~/helpers/getUtmParamsFromCookie';
import ftrAttribution from '~/helpers/mapFtrAttribution';
import { getSiteData, getSiteKey } from '~/helpers/site';
import { APOptions } from '~/types/order';
import { ReduxState } from '~/types/redux';
import { getCookie, setCookie } from '../../helpers/cookie';

let correlationId;
const getCorrelationId = () => {
  if (!correlationId) correlationId = uuid();
  return correlationId;
};

let testId = '';
const isTest = getCookie('isTest') === 'true';
if (isTest) {
  testId = '-playwright';
}

const getClientSideTrackingIds = () => {
  const clientSideTrackingIds = Cookies.get('clientSideTrackingIds');

  if (clientSideTrackingIds) return JSON.parse(clientSideTrackingIds);

  const clientSessionId = uuid();
  const orderId = uuid();
  const trackingIds = { clientSessionId, orderId, correlationId: getCorrelationId() };
  setCookie('clientSideTrackingIds', JSON.stringify(trackingIds), { otGroup: 'C0004' });
  return trackingIds;
};

const getSessionState = (sessionStorageState: string) => {
  const sessionState = JSON.parse(sessionStorageState) as ReduxState;

  // Remove keys that should not be persisted.
  const blocklist = ['autoSubmitAddress', 'notifications', 'pageLoading', 'modal'];
  blocklist.forEach((key) => delete sessionState[key]);

  return sessionState;
};

const getReturnVisitorLTOEligibility = (state: ReduxState) => {
  if (!clientSide) return;
  const hasQuoteId = getQuoteId();
  const sessionCount = Cookies.get('tglr_sess_count');
  const hasVisitedCart = Boolean(sessionCount && Number(sessionCount) > 1);

  const utmParams = getUtmParamsFromCookie();
  const hasVrcParam = Boolean((utmParams?.utm_content || '') === 'rvltovrc100');

  state.vrcLTOEligible = Boolean(hasQuoteId || hasVisitedCart || hasVrcParam);
};

export const getDefaultState = (production: boolean): ReduxState => {
  const trackingIds = getClientSideTrackingIds();
  const state: ReduxState = {
    modal: {
      activeModal: null,
      modalProduct: undefined,
      modalData: undefined,
    },
    production,
    tracking: {
      ...trackingIds,
      ...ftrAttribution(),
      clientId: `fuel-cart-${process.env.environment}-${getSiteKey()}${testId}`,
      writeKey: testId ? DEV_WRITE_AND_SOURCE_KEYS.writeKey : getSiteData('writeKey'),
      orderSessionId: trackingIds.clientSessionId,
      siteKey: getSiteKey(),
      monarchSourceId: MONARCH_SOURCE_ID,
      sourceKey: testId ? DEV_WRITE_AND_SOURCE_KEYS.sourceKey : getSiteData('sourceKey'),
      monarchToken: MONARCH_TOKEN,
      retryAttempts: '0',
    },
    discountOptions: {
      tracking: [],
    },
    address: {},
    addressHistory: [],
    autoSubmitAddress: false,
    autoPay: {
      accepted: true,
      methodSelected: APOptions.DEBIT,
      methodEntered: undefined,
      discountChangeAccepted: false,
      error: false,
    },
    upfrontCharge: {
      methodSelected: APOptions.DEBIT,
      methodEntered: undefined,
    },
    paperBill: false,
    monarch: {},
    preamp: {},
    callInNumber: '',
    amexOffer: false,
    cookiePass: false,
    sessionExpired: false,
    hideFrontierLogo: false,
    quiz: {
      currentQuestionIndex: 0,
    },
    otp: {
      success: false,
      codesGenerated: 0,
      codeVerifyAttempts: 0,
      verificationError: false,
      verificationRequiredFromQuote: false,
      attemptsPerCode: {
        1: 0,
        2: 0,
      },
    },
    creditFreeze: {
      creditFreezeStage: false,
      creditFreezeLifted: false,
      creditFreezeSubmissionCount: 0,
    },
    hideDebug: false,
    legal: {
      activeLegal: [],
      legalMap: {},
    },
    viewedProducts: [],
    resume: {},
    installationOptions: {},
    appointment: null,
    notifications: [],
    customOffers: {},
    vrcLTOEligible: false,
    breadcrumbsControl: {
      accordionStep: 0,
      stepChangeTo: 0,
    },
    currentlyInNGC: false,
    chatbot: {
      status: 'DISABLED',
      triggerType: null,
      proactiveModalViewed: {},
      gigaEventTriggered: {},
      vasCounter: 0,
      productCounter: 0,
      vasTriggeredPages: {},
      productTriggeredPages: {},
    },
    sessionSummary: undefined,
    offerMerchData: null,
  };

  //get the quoteId and add it for the buy/fiber flow
  const quoteId = getQuoteId();
  if (quoteId) {
    state.tracking.quoteId = quoteId;
  }
  return state;
};

// ACP (Affordable Connectivity Program) Querystring
const getACPQuerystring = (state: ReduxState) => {
  if (clientSide) {
    const searchParams = new URLSearchParams(document.location.search);

    // ACP Querystring
    const addressKey = searchParams.get('a');
    const controlNumber = searchParams.get('c');
    const env = searchParams.get('e');

    searchParams.delete('a');
    searchParams.delete('c');
    searchParams.delete('e');

    const params = Array.from(searchParams).length ? `?${searchParams.toString()}` : ``;
    window.history.replaceState({}, '', `${document.location.pathname}${params}`);

    if (env && controlNumber) {
      state.acp = { addressKey, env, controlNumber };
    }
  }
};

// Check for initial address set by cookie
const getAddressFromCookie = (state: ReduxState) => {
  const siteDetailCookie = Cookies.get('frontierSiteDetailPredictive');
  const siteDetails = siteDetailCookie ? JSON.parse(siteDetailCookie) : {};

  if (siteDetails?.address?.addressKey) {
    const {
      addressKey,
      addressLine1,
      addressLine2,
      city,
      stateProvince,
      postalCode,
      zipCode,
      zipCode4 = '',
      latitude,
      longitude,
      parsedAddress,
      env,
      controlNumber,
      rawUserString = '',
      isParent,
      parentKey,
      inFootprint,
    } = siteDetails.address;

    state.address = {
      install: {
        addressKey,
        address: addressLine1,
        address2: addressLine2,
        city,
        state: stateProvince,
        postalCode: postalCode,
        zip: zipCode,
        zip4: zipCode4,
        latitude: latitude,
        longitude: longitude,
        parsedAddress: parsedAddress,
        env,
        controlNumber,
        rawUserInput: rawUserString,
        isParent: isParent,
        parentKey: parentKey,
        inFootprint: inFootprint,
      },
    };
  }

  state.cookiePass = Boolean(Object.keys(siteDetails).length > 0);
};

const getInitialState = (production: boolean): ReduxState => {
  const sessionStorageState = clientSide && window.sessionStorage.getItem('persist:fuel');
  const state = sessionStorageState ? getSessionState(sessionStorageState) : getDefaultState(production);

  // append the return visitor eliglibility
  getReturnVisitorLTOEligibility(state);

  // append ACP param values to state
  getACPQuerystring(state);

  // If we have an address stored in a cookie auto populate it.
  getAddressFromCookie(state);

  return state;
};

export default getInitialState;